"use client";

import { useCallback, useEffect, useState } from "react";

import type { UseSidebarCollapseOptions, UseSidebarCollapseResult } from "./types";

export function useSidebarCollapse(
  options: UseSidebarCollapseOptions = {},
): UseSidebarCollapseResult {
  const {
    storageKey = "sidebar-collapsed",
    mobileBreakpoint = 768,
    defaultCollapsed = false,
    onCollapseChange,
  } = options;

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored !== null) return stored === "true";
    } catch {
      // SSR or localStorage unavailable
    }
    return defaultCollapsed;
  });

  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(`(max-width: ${mobileBreakpoint - 1}px)`).matches;
  });

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${mobileBreakpoint - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [mobileBreakpoint]);

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(storageKey, String(next));
      } catch {
        // noop
      }
      onCollapseChange?.(next);
      return next;
    });
  }, [storageKey, onCollapseChange]);

  return { collapsed, isMobile, toggle };
}
